import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { aspectMatchedViewBoxHeight, svgViewBoxPoint } from "@/lib/svgViewport";
import { Input } from "@/components/ui/input";
import type { MapsFrontendFailure } from "@/lib/mapsFrontendLoader";
import { mercatorProject, mercatorUnproject, type RoofCoordinate } from "../../../../shared/commercial/roofGeometry";

const WIDTH = 900;
const INITIAL_HEIGHT = 520;
const SITE_ZOOM = 4;
const PNOA_WMS = "https://www.ign.es/wms-inspire/pnoa-ma";
type Point = { latitude: number; longitude: number };
type Gesture = { mode: "map" | "pin"; pointerId: number; startX: number; startY: number; centerX: number; centerY: number; moved: boolean; startDraft: Point | null };
type MapGeometry = { projected: [number, number]; spanX: number; spanY: number; minX: number; maxX: number; minY: number; maxY: number };

function failureText(failure: MapsFrontendFailure | null): string {
  switch (failure) {
    case "configuration": return "Google Maps no está configurado.";
    case "script_timeout": return "La carga de Google Maps superó los 10 segundos; esto no demuestra un rechazo del servicio.";
    case "script_load_failed": return "No se pudo cargar el script de Google Maps.";
    case "authentication": return "Google Maps notificó un fallo de autenticación.";
    case "initialization": return "Google Maps cargó, pero no pudo inicializar el mapa.";
    default: return "Google Maps no está disponible.";
  }
}

export function PnoaPinFallback({
  value,
  center: previewCenter = null,
  onChange,
  disabled,
  failure,
  onSearchAddress,
}: {
  value: Point | null;
  center?: Point | null;
  onChange: (point: Point) => void;
  disabled: boolean;
  failure: MapsFrontendFailure | null;
  onSearchAddress?: () => void;
}) {
  const anchor = value ?? previewCenter;
  const anchorKey = anchor ? `${anchor.latitude},${anchor.longitude}` : "";
  const [viewportCenter, setViewportCenter] = useState<RoofCoordinate | null>(() => anchor ? [anchor.longitude, anchor.latitude] : null);
  const [zoom, setZoom] = useState(SITE_ZOOM);
  const [mapHeight, setMapHeight] = useState(INITIAL_HEIGHT);
  const previousAnchor = useRef(anchorKey);
  const [visualPan, setVisualPan] = useState<[number, number]>([0, 0]);
  const [latitude, setLatitude] = useState(value ? String(value.latitude) : "");
  const [longitude, setLongitude] = useState(value ? String(value.longitude) : "");
  const [draftPin, setDraftPin] = useState<Point | null>(null);
  const [error, setError] = useState("");
  const [loadedUrl, setLoadedUrl] = useState("");
  const [failedUrl, setFailedUrl] = useState("");
  const gesture = useRef<Gesture | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const imageUrlRef = useRef("");

  const geometry = useMemo<MapGeometry | null>(() => {
    if (!viewportCenter) return null;
    const projected = mercatorProject(viewportCenter);
    const spanX = 4_000 / zoom;
    const spanY = spanX * mapHeight / WIDTH;
    return {
      projected,
      spanX,
      spanY,
      minX: projected[0] - spanX / 2,
      maxX: projected[0] + spanX / 2,
      minY: projected[1] - spanY / 2,
      maxY: projected[1] + spanY / 2,
    };
  }, [mapHeight, viewportCenter, zoom]);
  const imageUrl = useMemo(() => {
    if (!geometry) return "";
    const params = new URLSearchParams({ SERVICE: "WMS", VERSION: "1.3.0", REQUEST: "GetMap", LAYERS: "OI.OrthoimageCoverage", STYLES: "", CRS: "EPSG:3857", BBOX: `${geometry.minX},${geometry.minY},${geometry.maxX},${geometry.maxY}`, WIDTH: String(WIDTH), HEIGHT: String(mapHeight), FORMAT: "image/png", TRANSPARENT: "false" });
    return `${PNOA_WMS}?${params}`;
  }, [geometry, mapHeight]);
  imageUrlRef.current = imageUrl;
  const loaded = Boolean(imageUrl) && loadedUrl === imageUrl;
  const tileFailed = Boolean(imageUrl) && failedUrl === imageUrl;

  useEffect(() => {
    if (anchorKey === previousAnchor.current) return;
    previousAnchor.current = anchorKey;
    setDraftPin(null);
    if (!anchor) {
      setViewportCenter(null);
      setVisualPan([0, 0]);
      return;
    }
    setViewportCenter([anchor.longitude, anchor.latitude]);
    setZoom(SITE_ZOOM);
    setVisualPan([0, 0]);
  }, [anchor?.latitude, anchor?.longitude, anchorKey]);

  useEffect(() => {
    if (value) {
      setLatitude(String(value.latitude));
      setLongitude(String(value.longitude));
    } else if (previewCenter) {
      setLatitude(String(previewCenter.latitude));
      setLongitude(String(previewCenter.longitude));
    } else {
      setLatitude("");
      setLongitude("");
    }
  }, [previewCenter, value?.latitude, value?.longitude]);

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const measure = () => {
      const rect = svg.getBoundingClientRect();
      const next = aspectMatchedViewBoxHeight(rect.width, rect.height, WIDTH, INITIAL_HEIGHT);
      setMapHeight(current => current === next ? current : next);
    };
    measure();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer?.observe(svg);
    window.addEventListener("resize", measure);
    return () => { observer?.disconnect(); window.removeEventListener("resize", measure); };
  }, [Boolean(geometry)]);

  function svgPointAt(clientX: number, clientY: number): [number, number] | null {
    const svg = svgRef.current;
    if (!svg || !geometry) return null;
    return svgViewBoxPoint(svg, clientX, clientY, WIDTH, mapHeight);
  }
  function coordinateAt(clientX: number, clientY: number): Point | null {
    if (!geometry) return null;
    const point = svgPointAt(clientX, clientY);
    if (!point) return null;
    const [x, y] = point;
    const [lng, lat] = mercatorUnproject([geometry.minX + x / WIDTH * geometry.spanX, geometry.maxY - y / mapHeight * geometry.spanY]);
    if (!Number.isFinite(lat) || !Number.isFinite(lng) || lat < -85 || lat > 85 || lng < -180 || lng > 180) return null;
    return { latitude: lat, longitude: lng };
  }
  function pointOnMap(point: Point): readonly [number, number] | null {
    if (!geometry) return null;
    const [x, y] = mercatorProject([point.longitude, point.latitude]);
    return [(x - geometry.minX) / geometry.spanX * WIDTH, (geometry.maxY - y) / geometry.spanY * mapHeight];
  }
  function previewPoint(point: Point) {
    setDraftPin(point);
    setLatitude(String(point.latitude));
    setLongitude(String(point.longitude));
  }
  function pointerDown(event: React.PointerEvent<SVGSVGElement>) {
    if (disabled || !loaded || !geometry) return;
    if (!svgPointAt(event.clientX, event.clientY)) return;
    event.preventDefault();
    const target = event.target as Element;
    gesture.current = { mode: target.closest("[data-pin-marker]") ? "pin" : "map", pointerId: event.pointerId, startX: event.clientX, startY: event.clientY, centerX: geometry.projected[0], centerY: geometry.projected[1], moved: false, startDraft: draftPin };
    if (typeof event.currentTarget.setPointerCapture === "function") event.currentTarget.setPointerCapture(event.pointerId);
  }
  function pointerMove(event: React.PointerEvent<SVGSVGElement>) {
    const active = gesture.current;
    if (!active || active.pointerId !== event.pointerId) return;
    if (active.mode === "pin") {
      const next = coordinateAt(event.clientX, event.clientY);
      if (next) previewPoint(next);
      return;
    }
    const start = svgPointAt(active.startX, active.startY);
    const current = svgPointAt(event.clientX, event.clientY);
    if (!start || !current || !geometry) return;
    const dx = current[0] - start[0];
    const dy = current[1] - start[1];
    if (Math.hypot(event.clientX - active.startX, event.clientY - active.startY) > 5) active.moved = true;
    if (active.moved) setVisualPan([dx, dy]);
  }
  function finishPointer(event: React.PointerEvent<SVGSVGElement>, cancelled = false) {
    const active = gesture.current;
    if (!active || active.pointerId !== event.pointerId) return;
    if (active.mode === "map" && active.moved && geometry) {
      const start = svgPointAt(active.startX, active.startY);
      const end = cancelled ? start : svgPointAt(event.clientX, event.clientY);
      if (start && end) {
        const dx = end[0] - start[0], dy = end[1] - start[1];
        setViewportCenter(mercatorUnproject([active.centerX - dx / WIDTH * geometry.spanX, active.centerY + dy / mapHeight * geometry.spanY]));
      }
      setVisualPan([0, 0]);
    } else if (active.mode === "pin") {
      if (cancelled) {
        if (active.startDraft) previewPoint(active.startDraft);
        else discardDraft();
      }
      else {
        const next = coordinateAt(event.clientX, event.clientY);
        if (next) previewPoint(next);
      }
    } else if (!cancelled) {
      const next = coordinateAt(event.clientX, event.clientY);
      if (next) previewPoint(next);
    }
    gesture.current = null;
  }
  function applyCoordinates() {
    const lat = Number(latitude), lng = Number(longitude);
    if (!latitude.trim() || !longitude.trim() || !Number.isFinite(lat) || !Number.isFinite(lng) || lat < -85 || lat > 85 || lng < -180 || lng > 180) {
      setError("Introduce latitud entre −85 y 85 y longitud entre −180 y 180.");
      return;
    }
    setError("");
    setDraftPin({ latitude: lat, longitude: lng });
    setViewportCenter([lng, lat]);
    setVisualPan([0, 0]);
  }

  function confirmDraft() {
    if (!draftPin || disabled) return;
    onChange(draftPin);
    setDraftPin(null);
  }

  function discardDraft() {
    setDraftPin(null);
    const resetTo = value ?? previewCenter;
    setLatitude(resetTo ? String(resetTo.latitude) : "");
    setLongitude(resetTo ? String(resetTo.longitude) : "");
  }

  const visiblePin = draftPin ?? value;
  const marker = visiblePin ? pointOnMap(visiblePin) : null;

  return <section className="space-y-3" aria-label="Mapa PNOA y ubicación manual">
    <p role="status" className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900">{failureText(failure)} La ortofoto es una alternativa; tocar, arrastrar o introducir coordenadas solo crea un borrador. Confirma la ubicación para aplicarla.</p>
    {onSearchAddress && <Button type="button" size="touch" variant="outline" disabled={disabled} onClick={onSearchAddress}>Buscar emplazamiento con CartoCiudad</Button>}
    {geometry ? <div className="overflow-hidden rounded-2xl bg-muted shadow-sm">
      <svg ref={svgRef} viewBox={`0 0 ${WIDTH} ${mapHeight}`} preserveAspectRatio="xMidYMid meet" className="block h-[clamp(380px,58vh,680px)] min-h-[380px] w-full touch-none bg-muted" role="application" aria-label="Mapa PNOA. Toca para previsualizar un pin o arrastra para mover el mapa." onPointerDown={pointerDown} onPointerMove={pointerMove} onPointerUp={finishPointer} onPointerCancel={event => finishPointer(event, true)}>
        {imageUrl && <image key={imageUrl} href={imageUrl} x={visualPan[0]} y={visualPan[1]} width={WIDTH} height={mapHeight} preserveAspectRatio="xMidYMid meet" onLoad={() => { if (imageUrlRef.current === imageUrl) setLoadedUrl(imageUrl); }} onError={() => { if (imageUrlRef.current === imageUrl) setFailedUrl(imageUrl); }} />}
        {!loaded && <text x={WIDTH / 2} y={mapHeight / 2} textAnchor="middle" fill="#17181B" fontSize="18">{tileFailed ? "PNOA no disponible · puedes introducir coordenadas" : "Cargando ortofoto PNOA…"}</text>}
        {marker && loaded && <g data-pin-marker="true" data-pin-state={draftPin ? "draft" : "confirmed"} transform={`translate(${visualPan[0]} ${visualPan[1]})`} style={{ cursor: disabled ? "default" : "grab" }}><path d={`M ${marker[0]} ${marker[1] + 19} C ${marker[0] - 3} ${marker[1] + 12}, ${marker[0] - 15} ${marker[1]}, ${marker[0] - 15} ${marker[1] - 9} A 15 15 0 1 1 ${marker[0] + 15} ${marker[1] - 9} C ${marker[0] + 15} ${marker[1]} ${marker[0] + 3} ${marker[1] + 12} ${marker[0]} ${marker[1] + 19} Z`} fill="#FF4D14" stroke="#17181B" strokeWidth="2"/><circle cx={marker[0]} cy={marker[1] - 9} r="5" fill="white"/></g>}
        <text x="14" y="28" fill="white" stroke="#17181B" strokeWidth=".8" fontSize="16">PNOA · © IGN/CNIG · CC BY 4.0</text>
      </svg>
    </div> : <div role="status" className="flex min-h-48 items-center justify-center rounded-2xl bg-muted p-6 text-center text-sm text-muted-foreground">Ubicación pendiente de resolver. No se muestra una zona predeterminada ni se coloca un pin automáticamente.</div>}
    <div className="flex flex-wrap items-center gap-2"><Button type="button" size="touch" variant="outline" disabled={!loaded || zoom <= 0.005} onClick={() => setZoom(z => Math.max(0.005, z / 1.5))} aria-label="Alejar PNOA">−</Button><Button type="button" size="touch" variant="outline" disabled={!loaded || zoom >= 12} onClick={() => setZoom(z => Math.min(12, z * 1.5))} aria-label="Acercar PNOA">+</Button><span className="text-xs text-muted-foreground">Arrastra para desplazarte · toca para previsualizar un pin.</span><a className="text-xs underline" href="https://www.ign.es/resources/licencia/Condiciones_licenciaUso_IGN.pdf" target="_blank" rel="noreferrer">Condiciones de uso IGN</a></div>
    <div className="grid gap-2 sm:grid-cols-[1fr_1fr_auto] sm:items-end"><label className="grid gap-1 text-sm">Latitud<Input controlSize="touch" inputMode="decimal" aria-label="Latitud" value={latitude} disabled={disabled} onChange={e => setLatitude(e.target.value)} /></label><label className="grid gap-1 text-sm">Longitud<Input controlSize="touch" inputMode="decimal" aria-label="Longitud" value={longitude} disabled={disabled} onChange={e => setLongitude(e.target.value)} /></label><Button type="button" size="touch" variant="outline" disabled={disabled} onClick={applyCoordinates}>Previsualizar coordenadas</Button></div>
    {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
    {draftPin && <div className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3"><p role="status" className="text-sm">Borrador PNOA: {draftPin.latitude.toFixed(6)}, {draftPin.longitude.toFixed(6)} · aún no aplicado.</p><Button type="button" size="touch" disabled={disabled} onClick={confirmDraft}>Confirmar ubicación</Button><Button type="button" size="touch" variant="outline" disabled={disabled} onClick={discardDraft}>Descartar borrador</Button></div>}
    <p className="text-xs text-muted-foreground">{viewportCenter ? `Centro PNOA: ${viewportCenter[1].toFixed(5)}, ${viewportCenter[0].toFixed(5)} · ${previewCenter && !value ? "emplazamiento CartoCiudad en previsualización" : value ? "emplazamiento con pin" : "ubicación pendiente de resolver"}` : "Centro PNOA: pendiente de resolver"}</p>
    {value && !draftPin && <p className="text-xs text-muted-foreground">Ubicación aplicada: {value.latitude.toFixed(6)}, {value.longitude.toFixed(6)}.</p>}
  </section>;
}
